/**
 * "Exportera" (för människor): flikarna Leads, Kunder, Kontakter och Om filen.
 *
 * Ren logik: tar emot data och returnerar flikar { namn, kolumner, rader }
 * som utils/leadsExcelExport.js skriver. Leadsraderna byggs i CRMLeads.js
 * (buildLeadExportRows) och kommer in färdiga.
 *
 * Kolumnflaggor som skrivaren förstår:
 *   bild   bilden i radens _bild (Storage-sökväg eller URL) bäddas in
 *   id     id-kolumn: läggs längst till höger, dold i en ihopfälld grupp
 *   wrap   radbrytning, maxbredd 60
 *   lank   webbadress blir klickbar
 *   fet    fetstil
 *   format talformat ('#,##0', '0 %', 'yyyy-mm-dd')
 *   stil   'status' (färg från _statusColor/_statusBg) | 'forfallen' (röd om _followUpOverdue)
 */
import { exportBildkalla } from './bilder';
import { urvalKundprojekt, kundprojektRader, kundSakerhetsfalt } from './kundprojektExport';

const kol = (key, header, extra = {}) => ({ key, header, ...extra });

/** Id-kolumner: alla *_id och *_ids (kontakt_ids, lead_ids). Samma regel i analysexporten. */
export const arIdKolumn = key => /_ids?$/.test(key);

export const LEAD_KOLUMNER = [
  kol('logo',          '',                  { bild: true }),
  kol('company',       'Företag',           { fet: true }),
  kol('title',         'Lead'),
  kol('status',        'Pipeline-status',   { stil: 'status' }),
  kol('storlek',       'Storlek'),
  kol('product',       'Produkt / Typ'),
  kol('depts',         'Affärsområden'),
  kol('owner',         'Ägare'),
  kol('creator',       'Skapad av'),
  kol('value',         'Värde (KSEK)',      { format: '#,##0' }),
  kol('unit',          'Enhet'),
  kol('probability',   'Sannolikhet',       { format: '0 %' }),
  kol('weighted',      'Viktat (KSEK)',     { format: '#,##0' }),
  kol('prismodell',    'Prismodell'),
  kol('deployment',    'Driftsättning'),
  kol('plan',          'Plan / Licenstyp'),
  kol('municipality',  'Kommun'),
  kol('nextFollowUp',  'Nästa uppföljning', { format: 'yyyy-mm-dd', stil: 'forfallen' }),
  kol('expectedClose', 'Förväntat avslut',  { format: 'yyyy-mm-dd' }),
  kol('contractEnd',   'Kontrakt till'),
  kol('offerStatus',   'Offert-status'),
  kol('demoStatus',    'Demo-status'),
  kol('sandbox',       'Sandbox'),
  kol('tags',          'Samarbete'),
  kol('orgNumber',     'Org.nr'),
  kol('city',          'Ort'),
  kol('website',       'Webbplats',         { lank: true }),
  kol('phone',         'Telefon'),
  kol('description',   'Beskrivning',       { wrap: true }),
  kol('offerCount',    'Antal offerter'),
  kol('offerter',      'Offerter',          { wrap: true }),
  kol('lead_id',         'lead_id',         { id: true }),
  kol('kund_id',         'kund_id',         { id: true }),
  kol('huvudkontakt_id', 'huvudkontakt_id', { id: true }),
  kol('kontakt_ids',     'kontakt_ids',     { id: true }),
];

const KUND_KOLUMNER = [
  kol('logo',          '',               { bild: true }),
  kol('namn',          'Kund',           { fet: true }),
  kol('org_nr',        'Org.nr'),
  kol('segment',       'Segment'),
  kol('organisationstyp', 'Organisationstyp'),
  kol('bransch',       'Bransch'),
  kol('ort',           'Ort'),
  kol('webbplats',     'Webbplats',      { lank: true }),
  kol('telefon',       'Telefon'),
  kol('anstallda',     'Anställda'),
  kol('omsattning',    'Omsättning'),
  kol('kundstatus',    'Kundstatus'),
  kol('affarsomraden', 'Affärsområden'),
  kol('linkedin',      'LinkedIn',       { lank: true }),
  kol('antal_leads',   'Leads i urvalet'),
  kol('erfarenhet_sakerhetsskydd', 'Erfarenhet av säkerhetsskydd'),
  kol('har_isolerad_miljo',        'Isolerad miljö'),
  kol('sakerhets_anteckning',      'Säkerhetsanteckning', { wrap: true }),
  kol('beskrivning',   'Beskrivning',    { wrap: true }),
  kol('kund_id',       'kund_id',        { id: true }),
];

const KUNDPROJEKT_KOLUMNER = [
  kol('namn',             'Kundprojekt',      { fet: true }),
  kol('status',           'Status'),
  kol('typ',              'Typ'),
  kol('ort',              'Ort'),
  kol('byggskede',        'Byggskede'),
  kol('skede_start',      'Skedestart'),
  kol('nasta_skede',      'Nästa skede'),
  kol('entreprenadform',  'Entreprenadform'),
  kol('storleksklass',    'Storleksklass'),
  kol('driftkrav',        'Driftkrav'),
  kol('verktyg_idag',     'Verktyg i dag'),
  kol('antal_granskare',  'Antal granskare'),
  kol('sakerhetsskydd',   'Säkerhetsskydd'),
  kol('it_miljo',         'IT-miljö'),
  kol('projektkontor',    'Projektkontor'),
  kol('sakerhetsskyddsavtal', 'Säkerhetsskyddsavtal'),
  kol('externa_parter_behover_atkomst', 'Externa parter behöver åtkomst'),
  kol('filoverforing',    'Filöverföring'),
  kol('granskning_idag',  'Granskning i dag', { wrap: true }),
  kol('parter',           'Parter',           { wrap: true }),
  kol('testkorningar',    'Testkörningar',    { wrap: true }),
  kol('kalla',            'Källa'),
  kol('extern_kalla_system', 'Extern källa'),
  kol('extern_kalla_url', 'Extern länk',      { lank: true }),
  kol('beskrivning',      'Beskrivning',      { wrap: true }),
  kol('skapad',           'Registrerad'),
  kol('skapad_av',        'Registrerad av'),
  kol('uppdaterad',       'Uppdaterad'),
  kol('uppdaterad_av',    'Uppdaterad av'),
  kol('kundprojekt_id',   'kundprojekt_id',   { id: true }),
  kol('registrerad_vid_lead_id', 'registrerad_vid_lead_id', { id: true }),
  kol('registrerad_vid_aktivitet_id', 'registrerad_vid_aktivitet_id', { id: true }),
  kol('extern_kalla_id',  'extern_kalla_id',  { id: true }),
];

const KUNDPROJEKTPART_KOLUMNER = [
  kol('_kundprojekt',   'Kundprojekt', { fet: true }),
  kol('_kund',          'Kund'),
  kol('roll',           'Roll'),
  kol('_leads',         'Leads',       { wrap: true }),
  kol('kundprojekt_id', 'kundprojekt_id', { id: true }),
  kol('kund_id',        'kund_id',        { id: true }),
  kol('lead_ids',       'lead_ids',       { id: true }),
];

const KUNDPROJEKTKONTAKT_KOLUMNER = [
  kol('_kundprojekt',      'Kundprojekt', { fet: true }),
  kol('_kontakt',          'Kontakt'),
  kol('_kund',             'Kund'),
  kol('roll_i_projektet',  'Roll i projektet'),
  kol('kundprojekt_id',    'kundprojekt_id', { id: true }),
  kol('kontakt_id',        'kontakt_id',     { id: true }),
];

const kontaktKolumner = medFoton => [
  ...(medFoton ? [kol('foto', '', { bild: true })] : []),
  kol('namn',         'Namn',           { fet: true }),
  kol('kund',         'Kund'),
  kol('roll',         'Roll'),
  kol('avdelning',    'Avdelning'),
  kol('epost',        'E-post'),
  kol('mobil',        'Mobil'),
  kol('telefon',      'Telefon'),
  kol('linkedin',     'LinkedIn',       { lank: true }),
  kol('saljroller',   'Säljroll'),
  kol('huvudkontakt', 'Huvudkontakt i'),
  kol('anteckningar', 'Anteckningar',   { wrap: true }),
  kol('kontakt_id',   'kontakt_id',     { id: true }),
  kol('kund_id',      'kund_id',        { id: true }),
];

const versal = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

/**
 * @param {object} d
 *   leadRader      färdiga rader från buildLeadExportRows (med _bild och id-fält)
 *   leads, companies, departments
 *   contactsByCompany {companyId: [contact]}
 *   kundstatusar   [{ key, label }]
 *   kundprojekt    alla kundprojekt (urvalet görs här), allaLeads, users
 *   medFoton       bädda in kontaktfoton
 *   meta           { exportedAt, exportedBy, filterSummary }
 */
export function byggVanligaBlad(d) {
  const {
    leadRader, leads, companies, departments = [], contactsByCompany = {},
    kundstatusar = [], medFoton = false, meta = {},
    kundprojekt = [], allaLeads = leads, users = [],
  } = d;
  const kundMap = new Map(companies.map(c => [c.id, c]));
  const kundIds = [...new Set(leads.map(l => l.companyId).filter(Boolean))];
  const ao = ids => departments.filter(x => (ids || []).includes(x.id)).map(x => x.name).join(', ');

  const kundRader = kundIds.map(id => kundMap.get(id)).filter(Boolean)
    .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'))
    .map(c => ({
      _bild: exportBildkalla(c, 'logo'),
      namn: c.name || '',
      org_nr: c.orgNumber || '',
      segment: c.segment || '',
      organisationstyp: c.organisationstyp || '',
      bransch: c.industry || '',
      ort: c.city || '',
      webbplats: c.website || '',
      telefon: c.phone || '',
      anstallda: c.employees || '',
      omsattning: c.revenue || '',
      kundstatus: kundstatusar.find(s => s.key === c.status)?.label || c.status || '',
      affarsomraden: ao(c.departmentIds),
      linkedin: c.linkedin || c.linkedinUrl || '',
      antal_leads: leads.filter(l => l.companyId === c.id).length,
      ...kundSakerhetsfalt(c),
      beskrivning: c.description || '',
      kund_id: c.id,
    }));

  const kontaktRader = [];
  for (const kund of kundRader) {
    const id = kund.kund_id;
    const kontakter = [...(contactsByCompany[id] || [])]
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'sv'));
    for (const c of kontakter) {
      const huvudFor = leads.filter(l => l.companyId === id && l.mainContactId === c.id);
      kontaktRader.push({
        _bild: medFoton ? exportBildkalla(c, 'foto') : '',
        namn: c.name || '',
        kund: kund.namn,
        roll: c.role || '',
        avdelning: c.department || '',
        epost: c.email || '',
        mobil: c.mobile || '',
        telefon: c.phone || '',
        linkedin: c.linkedin || '',
        saljroller: (Array.isArray(c.salesRoles) ? c.salesRoles : [c.dealRelation].filter(Boolean)).map(versal).join(', '),
        huvudkontakt: huvudFor.map(l => l.title).filter(Boolean).join(', '),
        anteckningar: c.notes || '',
        kontakt_id: c.id,
        kund_id: id,
      });
    }
  }

  const kp = kundprojektRader({
    kundprojekt: urvalKundprojekt(kundprojekt, kundIds), companies, leads: allaLeads, contactsByCompany, users,
  });

  const stamp = (meta.exportedAt || new Date()).toLocaleString('sv-SE', { dateStyle: 'long', timeStyle: 'short' });
  const om = [
    { rubrik: 'LagbasN', text: 'Affärer – Pipeline' },
    { rubrik: 'Exporterad', text: [stamp, meta.exportedBy && `av ${meta.exportedBy}`].filter(Boolean).join(' ') },
    { rubrik: 'Urval', text: `${leadRader.length} ${leadRader.length === 1 ? 'lead' : 'leads'}. Filter: ${meta.filterSummary || 'inga'}` },
    { rubrik: '', text: '' },
    { rubrik: 'Leads', text: 'En rad per lead i urvalet, med kundens logotyp i första kolumnen.' },
    { rubrik: 'Kunder', text: 'En rad per kund som har minst ett lead i urvalet.' },
    { rubrik: 'Kontakter', text: `Alla kontakter hos kunderna${medFoton ? ', med foto i första kolumnen' : ''}.` },
    { rubrik: 'Kundprojekt', text: 'Projekt som kunderna driver eller deltar i, registrerade som säljunderlag (inte Tikabs egna uppdrag) – de där minst en part är en kund i urvalet.' },
    { rubrik: 'Kundprojektparter', text: 'En rad per part i ett kundprojekt: kund, roll och kopplade leads.' },
    { rubrik: 'Kundprojektkontakter', text: 'Kontakter kopplade till kundprojekten, med roll i projektet.' },
    { rubrik: '', text: '' },
    { rubrik: 'Dolda id-kolumner', text: 'Id-kolumnerna (lead_id, kund_id, huvudkontakt_id, kontakt_ids, kontakt_id, kundprojekt_id, lead_ids) ligger längst till höger i varje flik, dolda i en ihopfälld grupp. Klicka på + ovanför kolumnrubrikerna för att visa dem. De binder ihop flikarna.' },
    { rubrik: 'Bilder', text: 'Logotyper och foton följer med raden när du sorterar och filtrerar. Saknas bild är cellen tom.' },
    { rubrik: 'Säkerhetsflaggor', text: 'Filen innehåller säkerhetsflaggor för kunder och kundprojekt (erfarenhet av säkerhetsskydd, isolerad miljö, säkerhetsskyddsklassning). De anger bara att något gäller, aldrig var, vad eller hur. Endast för internt bruk.' },
    { rubrik: 'Personuppgifter', text: 'Filen innehåller namn, e-post och telefon till kundernas medarbetare. Endast för internt bruk inom Tikab.' },
  ];

  return [
    { namn: 'Leads', kolumner: LEAD_KOLUMNER, rader: leadRader, frysKolumner: 3,
      summa: { etikett: 'company', keys: ['value', 'weighted'] } },
    { namn: 'Kunder', kolumner: KUND_KOLUMNER, rader: kundRader, frysKolumner: 2 },
    { namn: 'Kontakter', kolumner: kontaktKolumner(medFoton), rader: kontaktRader, frysKolumner: medFoton ? 2 : 1 },
    { namn: 'Kundprojekt', kolumner: KUNDPROJEKT_KOLUMNER, rader: kp.kp, frysKolumner: 1 },
    { namn: 'Kundprojektparter', kolumner: KUNDPROJEKTPART_KOLUMNER, rader: kp.parter, frysKolumner: 1 },
    { namn: 'Kundprojektkontakter', kolumner: KUNDPROJEKTKONTAKT_KOLUMNER, rader: kp.kontakter, frysKolumner: 1 },
    { namn: 'Om filen', kolumner: [kol('rubrik', 'Om filen', { fet: true }), kol('text', '', { wrap: true, bredd: 100 })], rader: om },
  ];
}
